import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const resolveName = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  if (typeof name === "object") return name.sk || name.en || Object.values(name)[0] || "";
  return String(name);
};

const SalesStatisticsReport = ({ data, location }) => {
  const { t } = useTranslation();
  const {
    productsReport = {}, // CHANGE: This is now the new object with product details
    currency,
    from,
    to,
    overall = {},
    overallItemPrice = {},
  } = data || {};
  let isSingleDay = false;
  if (from === to) {
    isSingleDay = true;
  }
  const { name, street, postcode, city, ico } = location || {};

  // CHANGE: Creating table rows for products with new VAT details
  const productRows = Object.keys(productsReport || {}).map((productId) => {
    const product = productsReport[productId];
    return (
      <TableRow key={productId}>
        <TableCell>{resolveName(product.name)}</TableCell>
        <TableCell align="right">{product.qty}</TableCell>{" "}
        <TableCell align="right">
          {product.itemPrice.toFixed(2)} {currency}
        </TableCell>
        <TableCell align="right">
          {product.excludeVat.toFixed(2)} {currency}
        </TableCell>
        <TableCell align="right">{product.vatRate} %</TableCell>{" "}
        <TableCell align="right">
          {product.vatValue.toFixed(2)} {currency}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {product.totalPriceWithVat.toFixed(2)} {currency}
        </TableCell>
      </TableRow>
    );
  });

  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [page, setPage] = React.useState(0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedProductRows = productRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // CHANGE: Export to CSV with new VAT details for each product
  const exportToCSV = () => {
    const csvRows = [];
    const headers = [
      t("pages.dsokReports.productName"),
      t("pages.dsokReports.quantity"),
      t("pages.dsokReports.itemPrice"),
      t("pages.dsokReports.base"),
      t("pages.dsokReports.vatRate"),
      t("pages.dsokReports.vatValue"),
      t("pages.dsokReports.total"),
    ];
    csvRows.push(headers.join(","));

    Object.keys(productsReport || {}).forEach((productId) => {
      const product = productsReport[productId];
      const row = [
        `"${resolveName(product.name).replace(/"/g, '""')}"`,
        product.qty,
        product.itemPrice.toFixed(2),
        product.excludeVat.toFixed(2),
        `${product.vatRate}%`,
        product.vatValue.toFixed(2),
        product.totalPriceWithVat.toFixed(2),
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sales_statistics_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Data for the VAT rate breakdown table for "overall"
  const overallVatRows = Object.keys(overall || {})
    .map((vatRateKey) => {
      if (vatRateKey === "overall") {
        return null;
      }
      const vatData = overall[vatRateKey];
      return (
        <TableRow key={vatRateKey}>
          <TableCell>{vatData.vatRate} %</TableCell>
          <TableCell align="right">
            {vatData.excludeVat.toFixed(2)} {currency}
          </TableCell>
          <TableCell align="right">
            {vatData.vatValue.toFixed(2)} {currency}
          </TableCell>
          <TableCell align="right">
            {vatData.total.toFixed(2)} {currency}
          </TableCell>
        </TableRow>
      );
    })
    .filter(Boolean);

  // Grand total from the "overall" object
  const overallGrandTotal = overall?.overall?.total?.toFixed(2);
  const overallGrandBase = overall?.overall?.excludeVat?.toFixed(2);
  const overallGrandVatValue = overall?.overall?.vatValue?.toFixed(2);

  // Grand total from the "overallItemPrice" object
  const overallItemPriceGrandTotal =
    overallItemPrice?.overall?.total?.toFixed(2);
  const overallItemPriceGrandBase =
    overallItemPrice?.overall?.excludeVat?.toFixed(2);
  const overallItemPriceGrandVatValue =
    overallItemPrice?.overall?.vatValue?.toFixed(2);

  // Data for the VAT rate breakdown table for "overallItemPrice"
  const overallItemPriceVatRows = Object.keys(overallItemPrice || {})
    .map((vatRateKey) => {
      if (vatRateKey === "overall") {
        return null;
      }
      const vatData = overallItemPrice[vatRateKey];
      return (
        <TableRow key={vatRateKey}>
          <TableCell>{vatData.vatRate} %</TableCell>
          <TableCell align="right">
            {vatData.excludeVat.toFixed(2)} {currency}
          </TableCell>
          <TableCell align="right">
            {vatData.vatValue.toFixed(2)} {currency}
          </TableCell>
          <TableCell align="right">
            {vatData.total.toFixed(2)} {currency}
          </TableCell>
        </TableRow>
      );
    })
    .filter(Boolean);

  return (
    <div>
      {/* Section for overall total */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          mb: 2,
        }}
      >
        <h3>
          {t("pages.dsokReports.totalOrders")}: {overallGrandTotal} {currency}
        </h3>
        <Tooltip title={t("pages.dsokReports.exportToCSV")}>
          <IconButton color="primary" onClick={exportToCSV} sx={{ ml: 2 }}>
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Table with VAT rate breakdown for "overall" */}
      {overallVatRows.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "left", mt: 2, mb: 2 }}>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {t("pages.dsokReports.vatRate")}
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.dsokReports.base")}
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.dsokReports.vat")}
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.dsokReports.total")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overallVatRows}
                <TableRow sx={{ fontWeight: "bold" }}>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    {t("pages.dsokReports.totalOverall")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {overallGrandBase} {currency}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {overallGrandVatValue} {currency}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {overallGrandTotal} {currency}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "left", mt: 2, mb: 2 }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("pages.dsokReports.productName")}</TableCell>
                <TableCell align="right">
                  {t("pages.dsokReports.quantity")}
                </TableCell>{" "}
                <TableCell align="right">
                  {t("pages.dsokReports.itemPrice")}
                </TableCell>{" "}
                <TableCell align="right">
                  {t("pages.dsokReports.base")}
                </TableCell>{" "}
                <TableCell align="right">
                  {t("pages.dsokReports.vatRate")}
                </TableCell>{" "}
                <TableCell align="right">
                  {t("pages.dsokReports.vatValue")}
                </TableCell>{" "}
                <TableCell align="right">
                  {t("pages.dsokReports.total")}
                </TableCell>{" "}
              </TableRow>
            </TableHead>
            <TableBody>{paginatedProductRows}</TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 40, 50, 100]}
            component="div"
            count={Object.keys(productsReport || {}).length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>
    </div>
  );
};

export default SalesStatisticsReport;
